import type { ComponentChildren } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import type { WeightTrend } from '~/calculations/weightTrends'
import { DecisionPanel } from '~/components/feedback/DecisionPanel'
import { Button } from '~/components/primitives/Button'
import { InfoButton } from '~/components/primitives/InfoButton'
import { buildAdherenceCheck } from '~/models/adherenceCheck'
import type { ContextTag } from '~/models/contextTags'
import { buildRecoveryCheck } from '~/models/recoveryCheck'
import type { RulesOutcome } from '~/models/rulesOpinion'
import type { WeightDisplayUnit } from '~/models/settings'
import type { TrainingSessionCounts } from '~/models/trainingWeek'
import type {
  WeeklyReviewDraftPatch,
  WeeklyReviewRecord,
  WeeklyReviewStep,
} from '~/models/weeklyReview'
import { resumeStepWithin } from '~/models/weeklyReview'
import { finalizeReview, saveReviewStep } from '~/services/reviewService'
import type { LiftWithSummary } from '~/services/strengthService'
import {
  markReviewAbandoned,
  markReviewFinalized,
  markReviewStarted,
  registerDraftSaver,
} from '~/services/updateService'
import { useDatabase } from '~/storage/DatabaseContext'
import { err, ok, type Result } from '~/utils/result'
import { StrengthStep } from './StrengthStep'
import type { AdherenceDraft, RecoveryDraft } from './steps'
import {
  AdherenceStep,
  NotesStep,
  NutritionStep,
  RecoveryStep,
  stepExplanation,
  TrendStep,
} from './steps'
import styles from './WeeklyReviewWizard.module.css'

/**
 * The guided weekly review (Checkpoint 2.1).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE DRAFT IS WRITTEN ON EVERY STEP TRANSITION, AND THAT IS THE POINT.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * At Checkpoint 1.8 a reload of the Review screen discarded unsaved readings —
 * found on a physical iPhone, where a backgrounded tab can be evicted without
 * warning. Here, moving between steps persists what the step collected, so the
 * worst any interruption costs is the step currently on screen.
 *
 * It also registers the first **real** draft saver (D-044). Until now
 * `registerDraftSaver` existed and was tested but was deliberately unused,
 * because nothing persisted a partial session and a saver that resolved without
 * writing would have reported work as saved that was not. The saver below
 * writes through the same path as a step transition and returns a typed
 * failure, so a failed save aborts the update rather than losing the week.
 */

const STEP_TITLES: Readonly<Record<WeeklyReviewStep, string>> = {
  trend: 'This week',
  measurements: 'Measurements',
  strength: 'Strength',
  recovery: 'How the week felt',
  nutrition: 'Nutrition',
  adherence: 'How closely you followed the plan',
  notes: 'Anything unusual',
  decision: "Project Sola's read",
}

interface WeeklyReviewWizardProps {
  review: WeeklyReviewRecord
  trend: WeightTrend
  unit: WeightDisplayUnit
  outcome: RulesOutcome | undefined
  /** The measurement capture form, which already exists and is reused whole. */
  measurementStep: ComponentChildren
  /** True while the measurement form holds unsaved readings. */
  measurementsPending: boolean
  lifts: readonly LiftWithSummary[]
  /**
   * The steps this user's review has (D-090).
   *
   * Passed in rather than computed here, because deciding it means reading the
   * project's domain goals and this component may not touch storage. The Review
   * page reads them once and hands the list down; `reviewStepsFor` in the model is
   * the single authority on what the list contains.
   */
  steps: readonly WeeklyReviewStep[]
  /** What the user calls their nutrition authority, for the weekly step only. */
  nutritionProviderLabel?: string
  /**
   * This week's own session counts, when it was materialised (D-105 §3).
   *
   * Read by the page, like `steps`, because deciding it means reading storage.
   * Present replaces the two training fields with what the week actually
   * contains; absent leaves a legacy week exactly as it was.
   */
  derivedTraining?: TrainingSessionCounts
  onFinalized: () => void
  onReload: () => void
}

export function WeeklyReviewWizard({
  review,
  trend,
  unit,
  outcome,
  measurementStep,
  measurementsPending,
  lifts,
  steps,
  nutritionProviderLabel,
  derivedTraining,
  onFinalized,
  onReload,
}: WeeklyReviewWizardProps) {
  const db = useDatabase()

  const [step, setStep] = useState<WeeklyReviewStep>(() => resumeStepWithin(review, steps))
  const [recovery, setRecovery] = useState<RecoveryDraft>(() => ({
    ...(review.recovery?.ratings ?? {}),
  }))
  const [optionalRecovery, setOptionalRecovery] = useState<RecoveryDraft>(() => ({
    ...(review.recovery?.optionalRatings ?? {}),
  }))
  const [adherence, setAdherence] = useState<AdherenceDraft>(() => adherenceDraftFrom(review))
  const [notes, setNotes] = useState(review.notes ?? '')
  const [tags, setTags] = useState<readonly ContextTag[]>(review.contextTags)

  const [errors, setErrors] = useState<readonly string[]>([])
  const [busy, setBusy] = useState(false)
  const [savedAt, setSavedAt] = useState<string | undefined>(undefined)

  const index = steps.indexOf(step)

  /* The latest answers, readable from a callback that was registered earlier.
     The draft saver is handed to the update service once; without this it would
     close over the state as it stood at registration and save a stale week. */
  const latest = useRef({ step, recovery, optionalRecovery, adherence, notes, tags })
  latest.current = { step, recovery, optionalRecovery, adherence, notes, tags }

  /**
   * Write the draft.
   *
   * `at` is the step to record as the resume position, and it is the step being
   * moved **to** rather than the one being left. Recording the step behind the
   * user sends them back one on every resume: they answered it, pressed
   * Continue, and would find themselves answering it again. The draft saver
   * passes nothing and so records wherever the user currently is.
   */
  const persist = useCallback(
    async (at?: WeeklyReviewStep): Promise<Result<void, string>> => {
      const patch = patchFrom(
        { ...latest.current, ...(at === undefined ? {} : { step: at }) },
        derivedTraining,
      )
      const written = await saveReviewStep(db, review.id, patch, new Date())
      if (!written.ok) return err(written.error.message)
      return ok(undefined)
    },
    [db, review.id, derivedTraining],
  )

  /* The update service takes a zero-argument saver, so the step override is not
     reachable from it — which is correct: an update mid-review must record where
     the user actually is, not where they were going. */
  const saveDraft = useCallback((): Promise<Result<void, string>> => persist(), [persist])

  /*
   * Update deferral (D-044).
   *
   * An open wizard is an active review for its whole lifetime, not only while a
   * field holds unsaved text: the user is part-way through a multi-step task and
   * a reload would drop them back at the resumed step mid-thought. Leaving the
   * wizard releases the deferral; finalizing releases it too, through
   * `markReviewFinalized`.
   */
  useEffect(() => {
    markReviewStarted()
    registerDraftSaver(saveDraft)
    return () => {
      registerDraftSaver(null)
      markReviewAbandoned()
    }
  }, [saveDraft])

  async function goTo(next: WeeklyReviewStep) {
    setBusy(true)
    const saved = await persist(next)
    setBusy(false)

    if (!saved.ok) {
      setErrors([saved.error])
      return
    }
    setErrors([])
    setSavedAt(new Date().toISOString())
    setStep(next)
  }

  async function finish() {
    setBusy(true)
    const saved = await persist()
    if (!saved.ok) {
      setBusy(false)
      setErrors([saved.error])
      return
    }

    const finalized = await finalizeReview(db, review.id, new Date())
    setBusy(false)

    if (!finalized.ok) {
      setErrors([finalized.error.message])
      return
    }

    markReviewFinalized()
    setErrors([])
    onFinalized()
    onReload()
  }

  const explanation = stepExplanation(step, { nutritionProviderLabel })

  const isLast = index === steps.length - 1
  const previous = index > 0 ? steps[index - 1] : undefined
  const next = isLast ? undefined : steps[index + 1]

  return (
    <section class={styles.wizard} aria-labelledby="wizard-step-title">
      <div class={styles.progress}>
        <p class={styles.progressLabel}>
          Step {index + 1} of {steps.length}
        </p>
        {savedAt === undefined ? null : <p class={styles.saveState}>Saved</p>}
      </div>

      <div class={styles.track} aria-hidden="true">
        {steps.map((each, position) => (
          <span
            key={each}
            class={`${styles.segment} ${
              position < index
                ? styles.segmentDone
                : position === index
                  ? styles.segmentCurrent
                  : ''
            }`}
          />
        ))}
      </div>

      {/*
        The information control sits BESIDE the title, never inside it (D-104
        §2). Content inside a heading joins its accessible name, and this heading
        is what labels the whole wizard section — a control inside it would
        rename the section on every step.

        Rendered here rather than by each step body so the header keeps one
        shape whatever is below it, and so a step with nothing worth explaining
        simply has no control rather than an empty one.
      */}
      <div class={styles.stepHeader}>
        <h2 id="wizard-step-title" class={styles.stepTitle}>
          {STEP_TITLES[step]}
        </h2>
        {explanation === undefined ? null : (
          <InfoButton topic={explanation.topic} title={explanation.title}>
            {explanation.body}
          </InfoButton>
        )}
      </div>

      <div class={styles.body}>
        <StepBody
          step={step}
          trend={trend}
          unit={unit}
          outcome={outcome}
          lifts={lifts}
          measurementStep={measurementStep}
          onReload={onReload}
          recovery={recovery}
          optionalRecovery={optionalRecovery}
          adherence={adherence}
          notes={notes}
          tags={tags}
          setRecovery={setRecovery}
          setOptionalRecovery={setOptionalRecovery}
          setAdherence={setAdherence}
          setNotes={setNotes}
          setTags={setTags}
          nutritionSummary={steps.includes(`nutrition`)}
          {...(nutritionProviderLabel === undefined ? {} : { nutritionProviderLabel })}
          {...(derivedTraining === undefined ? {} : { derivedTraining })}
        />
      </div>

      {errors.length > 0 ? (
        <div class={styles.errors} role="alert">
          {errors.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      ) : null}

      {/* Warned about rather than blocked: unsaved readings are the user's to
          keep or discard, and a wizard that refuses to advance would be worse
          than one that says what will happen. */}
      {step === 'measurements' && measurementsPending ? (
        <p class={styles.pending}>
          You have readings typed in that are not saved yet. Save the measurements before
          continuing, or they will not be part of this week.
        </p>
      ) : null}

      <div class={styles.actions}>
        {previous === undefined ? null : (
          <Button variant="secondary" onClick={() => void goTo(previous)} disabled={busy}>
            Back
          </Button>
        )}
        {next === undefined ? (
          <Button onClick={() => void finish()} busy={busy}>
            Finish this review
          </Button>
        ) : (
          <Button onClick={() => void goTo(next)} busy={busy}>
            Continue
          </Button>
        )}
      </div>
    </section>
  )
}

/* ---- Step bodies --------------------------------------------------------- */

type Setter<T> = (update: (current: T) => T) => void

interface StepBodyProps {
  step: WeeklyReviewStep
  trend: WeightTrend
  unit: WeightDisplayUnit
  outcome: RulesOutcome | undefined
  lifts: readonly LiftWithSummary[]
  measurementStep: ComponentChildren
  onReload: () => void
  recovery: RecoveryDraft
  optionalRecovery: RecoveryDraft
  adherence: AdherenceDraft
  notes: string
  tags: readonly ContextTag[]
  setRecovery: Setter<RecoveryDraft>
  setOptionalRecovery: Setter<RecoveryDraft>
  setAdherence: Setter<AdherenceDraft>
  setNotes: (value: string) => void
  setTags: Setter<readonly ContextTag[]>
  /** True when the weekly nutrition step is in this list, so adherence omits it. */
  nutritionSummary: boolean
  nutritionProviderLabel?: string
  derivedTraining?: TrainingSessionCounts
}

/**
 * Which step is on screen.
 *
 * Split out of the wizard so that adding a step — 2.2 added strength, and
 * Checkpoint 3 adds photos — does not push the component that owns the draft,
 * the deferral and the finalize past a complexity the linter flags. The state
 * still lives in the wizard; only the rendering moved.
 */
function StepBody(props: StepBodyProps) {
  const { step } = props

  if (step === 'trend') return <TrendStep trend={props.trend} unit={props.unit} />
  if (step === 'measurements') return <>{props.measurementStep}</>
  if (step === 'strength') return <StrengthStep lifts={props.lifts} onChanged={props.onReload} />

  if (step === 'recovery') {
    return (
      <RecoveryStep
        ratings={props.recovery}
        optional={props.optionalRecovery}
        onChange={(item, value) => props.setRecovery((current) => ({ ...current, [item]: value }))}
        onChangeOptional={(item, value) =>
          props.setOptionalRecovery((current) => ({ ...current, [item]: value }))
        }
      />
    )
  }

  if (step === 'nutrition') {
    return (
      <NutritionStep
        draft={props.adherence}
        providerLabel={props.nutritionProviderLabel}
        onChange={(field, value) =>
          props.setAdherence((current) => ({ ...current, [field]: value }))
        }
      />
    )
  }

  if (step === 'adherence') {
    return (
      <AdherenceStep
        draft={props.adherence}
        showNutrition={!props.nutritionSummary}
        {...(props.derivedTraining === undefined ? {} : { derivedTraining: props.derivedTraining })}
        onChange={(field, value) =>
          props.setAdherence((current) => ({ ...current, [field]: value }))
        }
      />
    )
  }

  if (step === 'notes') {
    return (
      <NotesStep
        notes={props.notes}
        tags={props.tags}
        onNotes={props.setNotes}
        onToggleTag={(tag) =>
          props.setTags((current) =>
            current.includes(tag) ? current.filter((each) => each !== tag) : [...current, tag],
          )
        }
      />
    )
  }

  return (
    <>
      {props.outcome === undefined ? null : <DecisionPanel outcome={props.outcome} />}
      <p class={styles.pending}>
        Photos arrive at Checkpoint 3. Finishing the review saves this week and the recommendation
        above, exactly as it stands now.
      </p>
    </>
  )
}

/* ---- Draft shaping ------------------------------------------------------- */

function adherenceDraftFrom(review: WeeklyReviewRecord): AdherenceDraft {
  const stored = review.adherence
  const text = (value: number | undefined) => (value === undefined ? '' : String(value))
  return {
    nutritionDaysOnPlan: text(stored?.nutritionDaysOnPlan),
    trainingSessionsPlanned: text(stored?.trainingSessionsPlanned),
    trainingSessionsCompleted: text(stored?.trainingSessionsCompleted),
    stepTargetDays: text(stored?.stepTargetDays),
    proteinTargetDays: text(stored?.proteinTargetDays),
  }
}

interface WizardState {
  readonly step: WeeklyReviewStep
  readonly recovery: RecoveryDraft
  readonly optionalRecovery: RecoveryDraft
  readonly adherence: AdherenceDraft
  readonly notes: string
  readonly tags: readonly ContextTag[]
}

/**
 * Turn what is on screen into a patch.
 *
 * A partly-answered recovery or adherence step is simply **not included** rather
 * than rejected. Saving a draft must never fail because the user has not
 * finished answering — that would make the autosave an obstacle, and the update
 * service would read it as a draft it could not save.
 */
export function patchFrom(
  state: WizardState,
  derivedTraining?: TrainingSessionCounts,
): WeeklyReviewDraftPatch {
  const recovery = buildRecoveryCheck({
    ratings: state.recovery,
    optionalRatings: state.optionalRecovery,
  })

  const numeric = (value: string) => (value.trim() === '' ? undefined : Number(value))
  /* The derived counts win where a materialised week exists, and the draft's own
     two fields are not even on screen in that case. They are stored on the
     review because a finished week's `adherence` IS its immutable snapshot
     (D-046) — which D-105 §3 names as the one permitted copy, since a review
     must still report what it was answered about after the week has moved on.
     **Nothing writes them back to a plan**, which is what "never a second
     authority" means. */
  const adherence = buildAdherenceCheck({
    nutritionDaysOnPlan: numeric(state.adherence.nutritionDaysOnPlan) as number,
    trainingSessionsPlanned:
      derivedTraining?.planned ?? (numeric(state.adherence.trainingSessionsPlanned) as number),
    trainingSessionsCompleted:
      derivedTraining?.completed ?? (numeric(state.adherence.trainingSessionsCompleted) as number),
    ...(numeric(state.adherence.stepTargetDays) === undefined
      ? {}
      : { stepTargetDays: numeric(state.adherence.stepTargetDays) as number }),
    ...(numeric(state.adherence.proteinTargetDays) === undefined
      ? {}
      : { proteinTargetDays: numeric(state.adherence.proteinTargetDays) as number }),
  })

  return {
    draftStep: state.step,
    notes: state.notes,
    contextTags: state.tags,
    ...(recovery.ok ? { recovery: recovery.value } : {}),
    ...(adherence.ok ? { adherence: adherence.value } : {}),
  }
}
